import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Logo from '@alltrails/shared/denali/components/Logo';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import { getPriceTotal, isTrial } from 'utils/PlanUtils';
import PlusLandingPageBase from 'components/LandingPages/PlusLandingPageBase';
import { UserId } from '@alltrails/shared/types/User';
import * as styles from './PlusLandingPage.module.scss';
import getTrialDurationText from './utils/getTrialDurationText';

type PlusLandingPageProps = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  plan: Plan;
  userId?: UserId;
};

const s = (chunks: string) => <s className={styles.s}>{chunks}</s>;

export default function PlusLandingPage({ currencyCode, plan, coupon, userId }: PlusLandingPageProps) {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  if (coupon && coupon.type !== 'FreeTrialCoupon') {
    const discountedPricePerYear = getPriceTotal(plan, coupon);

    return (
      <PlusLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        headline={<FormattedMessage defaultMessage="Limited time only" />}
        subhead={
          discountedPricePerYear === 0 ? (
            <FormattedMessage
              defaultMessage="Just {discountedPricePerYear} for one year"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear)
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Just <s>{priceTotal}</s> {discountedPricePerYear} for one year (that’s {monthlyCost} a month)"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear),
                monthlyCost: formatter.format(discountedPricePerYear / 12),
                priceTotal: formatter.format(Number(plan.priceTotal)),
                s
              }}
            />
          )
        }
        title={
          <FormattedMessage defaultMessage="Save {percent, number, ::percent} on AllTrails+" values={{ percent: coupon.discountPercentage / 100 }} />
        }
        userId={userId}
        plan={plan}
      />
    );
  }

  const annualCost = Number(plan.priceTotal);
  const monthlyCost = Number(plan.priceMonth);
  const headline = <Logo size="sm" color="dark" variant="plus-logomark" />;
  if (isTrial(plan, coupon)) {
    const trialDurationText = getTrialDurationText(coupon?.freeTrialDuration);
    return (
      <PlusLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        coupon={coupon}
        headline={headline}
        subhead={
          <FormattedMessage
            defaultMessage="Then only {annualCost} a year (that’s {monthlyCost} a month)"
            values={{
              annualCost: formatter.format(annualCost),
              monthlyCost: formatter.format(monthlyCost)
            }}
          />
        }
        title={trialDurationText}
        userId={userId}
        plan={plan}
      />
    );
  }

  return (
    <PlusLandingPageBase
      buttonText={<FormattedMessage defaultMessage="Get started" />}
      headline={headline}
      subhead={
        <FormattedMessage
          defaultMessage="Only {annualCost} a year (that’s {monthlyCost} a month)"
          values={{
            annualCost: formatter.format(annualCost),
            monthlyCost: formatter.format(monthlyCost)
          }}
        />
      }
      title={<FormattedMessage defaultMessage="Confidence that travels light" />}
      userId={userId}
      plan={plan}
    />
  );
}
